<template>
	<v-app id="app" v-resize="onResize">
		<div
			:style="backgroundImage"
			:class="backgroundBlendMode"
		>
			<div
				:style="backgroundGradient"
				class="full-height"
			>
				<TheAppbar
					v-if="isMainRoute"
					:bar-color="inlineStyles.colors.secondary"
					:windowSize="windowSize"
				/>
				<!-- Pass Style to Other Router Views -->
				<router-view
					:inline-styles="inlineStyles"
					:windowSize="windowSize"
				/>
			</div>
		</div>
	</v-app>
</template>

<script>
import TheAppbar from "@/components/TheAppbar"

export default {
	name: "App",
	components: { TheAppbar },
	data: () => ({
		backgroundImage: {},
		dailyToken: 0,
		gradientPositions: {
			center: "",
			corner: ""
		},
		inlineStyles: {
			characterGlow: {},
			colors: {
				curriculum: "",
				main: "",
				secondary: ""
			},
			greetingText: {}
		},
		specialDays: [],
		windowSize: {
			width: window.innerWidth,
			height: window.innerHeight,
		}
	}),

	computed: {
		backgroundBlendMode() {
			return( this.isMainRoute ? "overlay-blend-mode" : "" )
		},

		backgroundGradient () {
			const gradient = { background: "" }

			if(this.isMainRoute) {
				const routes = {
					Home: {
						gradientDegrees: { desktop: "90", mobile: "180" },
						gradientPosition: this.gradientPositions.corner
					},
					Portfolio: {
						gradientDegrees: { desktop: "90", mobile: "0" },
						gradientPosition: this.gradientPositions.center
					},
					Contact: {
						gradientDegrees: { desktop: "270", mobile: "0" },
						gradientPosition: this.gradientPositions.corner
					}
				}

				const device = this.windowSize.width < 600 ?
					"mobile" : "desktop"

				const gradientDegree =
					routes[this.$route.name].gradientDegrees[device]

				const gradientPosition =
					routes[this.$route.name].gradientPosition

				gradient.background = `linear-gradient(
					${gradientDegree}deg, ${gradientPosition}
				)`.replace(/\t/g, "").replace(/\n/g, "")
			}else {
				gradient.background = "none"
			}

			return gradient
		},

		isMainRoute() {
			switch(this.$route.name){
				case "Home":
				case "Portfolio":
				case "Contact":
					return true
				default:
					return false
			}
		}
	},

	created() {
		this.fillStyleAttributes()
		this.onResize()
	},

	methods: {
		fillStyleAttributes() {
			this.generateDailyToken()

			const backgroundPatterns = [
				{
					name: "seamless",
					functionName: "generateSeamlessBackground",
					images: [
						{id: 0, maxSize: 6, minSize: 3},
						{id: 1, maxSize: 8, minSize: 3},
						{id: 2, maxSize: 13, minSize: 7},
						{id: 3, maxSize: 4, minSize: 2}
					]
				}
			]

			const hueValues = [
				0, 100, 110, 120, 130, 140, 150,
				160, 170, 180, 190, 200, 210, 220,
				230, 240, 250, 260, 270, 280, 290,
				300, 310, 320, 330, 340, 350, 360
			]

			const saturationValues = [30, 40, 50]

			const lightnessValues = [20, 30]

			const pattern = backgroundPatterns[
				this.dailyToken % backgroundPatterns.length
			]

			const randomHue = hueValues[
				this.dailyToken % hueValues.length
			]

			const randomSaturation = saturationValues[
				this.dailyToken % saturationValues.length
			]

			const randomLightness = randomSaturation == 30
				? 20
				: lightnessValues [ // If randomSaturation == 40 or 50
						this.dailyToken % lightnessValues.length
					]

			this.inlineStyles.colors.main = `hsl(
				${randomHue} ${randomSaturation}% ${randomLightness}%
			)`.replace(/\t/g, "").replace(/\n/g, "")
			
			this.inlineStyles.colors.curriculum = `hsl(
				${randomHue} ${randomSaturation}% ${randomLightness}% / 0.95
			)`.replace(/\t/g, "").replace(/\n/g, "")
			
			this.inlineStyles.colors.secondary = `hsl(
				${randomHue} ${randomSaturation + 50}% ${randomLightness + 50}%
			)`.replace(/\t/g, "").replace(/\n/g, "")

			this.gradientPositions.center = `
				transparent -100%, 
				${this.inlineStyles.colors.main} 40%, 
				${this.inlineStyles.colors.main} 60%, 
				transparent 200%
			`.replace(/\t/g, "").replace(/\n/g, "")

			this.gradientPositions.corner = `
				${this.inlineStyles.colors.main} 33%, transparent 150%
			`.replace(/\t/g, "").replace(/\n/g, "")

			this.inlineStyles.characterGlow = {
				filter: `drop-shadow(0 0 20px hsl(
					${randomHue} ${randomSaturation}% ${randomLightness + 30}%
				))`.replace(/\t/g, "").replace(/\n/g, ""),
			}

			this.inlineStyles.greetingText = {
				fontWeight: "bold",
				color: `hsl(
					${randomHue} ${randomSaturation + 50}% ${randomLightness + 20}%
				)`.replace(/\t/g, "").replace(/\n/g, ""),
				textShadow: "2px 2px 4px black",
				webkitTextStroke: "2px black",
			}

			this[pattern.functionName](pattern)
		},
		
		generateDailyToken() {
			const today = new Date()
			// Weekday Month Day Year 00:00:00
			const todayAtMidnight = today.toDateString()
			const todayInMilliseconds = Date.parse(todayAtMidnight)

			const milliseconsWithNoZeros =
				String(todayInMilliseconds).split("").filter(
					number => number != 0 
				)

			const millisecondsSum =
				milliseconsWithNoZeros.reduce(
					(previousNum, currentNum) =>
						Number(previousNum) + Number(currentNum), 0
				)

			const millisecondsMultiplicationAndDivision =
				milliseconsWithNoZeros.reduce(
					(previousNum, currentNum, index) => {
						if(index%2 == 0) { // Pair Index
							return Number(previousNum) / Number(currentNum)
						}else { // Odd Index
							return Number(previousNum) * Number(currentNum)
						}
					}, 1
				)

			const millisecondsPow =
				Math.pow(
					millisecondsSum,
					millisecondsMultiplicationAndDivision
				)

			const millisecondsNoDecimals =
				String(millisecondsPow).replace(".", "")

			this.dailyToken = Number(
				millisecondsNoDecimals.slice(
					0, milliseconsWithNoZeros.length
				)
			)

			// console.log(todayInMilliseconds)
			// console.log(this.dailyToken)
		},

		generateSeamlessBackground(pattern) {
			// Select Random File of Selected Folder
			const image = pattern.images[
				this.dailyToken % pattern.images.length
			]

			const patternSizeRange = image.maxSize - image.minSize
			const patternSizeStep = this.dailyToken % patternSizeRange
			const patternSize = (image.minSize + patternSizeStep) * 10

			this.backgroundImage = {
				background: `
					url(${require(
						"@/assets/images/backgrounds/"+pattern.name+"/"+image.id+".webp"
					)}), 
					${this.inlineStyles.colors.main}
				`.replace(/\t/g, "").replace(/\n/g, ""),
				backgroundSize: `${patternSize}px`,
				height: "100%"
			}
		},

		onResize() {
			this.windowSize.width = window.innerWidth
			this.windowSize.height = window.innerHeight
		}
	}
}
</script>

<style lang="scss">
#app {
	font-family: Avenir, Helvetica, Arial, sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	color: white;
	overflow: hidden
}

.full-height {
	height: 100%;
}

.overlay-blend-mode {
	background-blend-mode: overlay;
}
</style>